import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { httpService } from "../../services/httpService";
import DynamicTable from "../common/DynamicTable";
import SupplierPaymentDrawer from "./SupplierPaymentDrawer";
import { MdPayments } from "react-icons/md";

// Add this helper function for date formatting
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const SupplierPayment = () => {
  const { supplierId } = useParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [formData, setFormData] = useState({
    vendor_purchase_id: "",
    paid_amount: "",
    payment_mode: "cash",
    payment_reference: "",
    payment_status: "paid"
  });
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Fetch invoices for dropdown and also used to map invoice_display
  useEffect(() => {
    if (!supplierId) return;
    httpService
      .get(`vendorpurchase/getVendorSupplierPurchases/${supplierId}`)
      .then((res) => {
        if (res.status && res.code === 200) {
          setInvoices(res.data);
        }
      })
      .catch(() => setInvoices([]));
  }, [supplierId]);

  // Fetch payments and enrich with invoice_display
  useEffect(() => {
    if (!supplierId) return;
    setLoading(true);
    httpService
      .get(`/vendorsupplierpayment/getVendorSupplierPayments/${supplierId}`)
      .then((res) => {
        if (res.status && res.code === 200) {
          const enriched = res.data.map(payment => {
            const invoice = invoices.find(inv => inv._id === payment.vendor_purchase_id);
            return {
              ...payment,
              invoice_display: invoice
                ? `${invoice.invoice_number} (${formatDate(invoice.invoice_date)})`
                : ""
            };
          });
          setPayments(enriched);
        }
      })
      .catch(() => setPayments([]))
      .finally(() => setLoading(false));
  }, [supplierId, showDrawer, invoices]);

  // DynamicTable columns for payments
  const columns = [
    {
      key: "invoice_display",
      header: "Invoice",
      sortable: true,
      renderCell: row => row.invoice_display || "N/A"
    },
    {
      key: "paid_amount",
      header: "Paid Amount",
      sortable: true,
      renderCell: row => `₹${row.paid_amount}`
    },
    {
      key: "payment_mode",
      header: "Mode",
      sortable: true,
      renderCell: row => {
        const mode = row.payment_mode || '';
        return mode.charAt(0).toUpperCase() + mode.slice(1);
      }
    },
    {
      key: "payment_reference",
      header: "Reference",
      sortable: true,
      renderCell: row => row.payment_reference || "-"
    },
    {
      key: "payment_status",
      header: "Status",
      sortable: true,
      renderCell: row => {
        const status = row.payment_status || 'unknown';
        const displayStatus = status.charAt(0).toUpperCase() + status.slice(1);
        
        // Determine background color based on status
        let bgColorClass = 'bg-gray-100 text-gray-700'; // default
        if (status === 'paid') {
          bgColorClass = 'bg-green-100 text-green-700';
        } else if (status === 'unpaid') {
          bgColorClass = 'bg-red-100 text-red-700';
        } else if (status === 'partial') {
          bgColorClass = 'bg-yellow-100 text-yellow-700';
        }
        
        return (
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${bgColorClass}`}>
            {displayStatus}
          </span>
        );
      }
    },
  ];

  // Add Payment action
  const actions = [
    {
      label: 'Add Payment',
      icon: <MdPayments className="w-5 h-5 text-gray-600 group-hover:text-green-600 transition-colors" />,
      onClick: row => {
        setSelectedPayment(row);
        setShowDrawer(true);
      },
      colorClasses: 'hover:border-green-500 hover:bg-green-50',
      shouldShow: () => true
    }
  ];

  const handleAddPayment = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError(null);
    try {
      await httpService.post("/vendorsupplierpayment/addVendorSupplierPayment", {
        supplier_id: supplierId,
        vendor_purchase_id: formData.vendor_purchase_id,
        paid_amount: Number(formData.paid_amount),
        payment_mode: formData.payment_mode,
        payment_reference: formData.payment_reference,
        //payment_status: formData.payment_status
      });
      setShowDrawer(false);
      setFormData({
        vendor_purchase_id: "",
        paid_amount: "",
        payment_mode: "cash",
        payment_reference: "",
        payment_status: "paid"
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add payment");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="bg-white p-4 min-h-[400px] ">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-slate-700">Supplier Payments</h3>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-full shadow"
          onClick={() => setShowDrawer(true)}
        >
          + Add Payment
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
        <DynamicTable 
          columns={columns} 
          data={payments} 
          showSearch={true}
          showPagination={true}
          showPageInfo={true}
          initialPageSize={10}
          emptyMessage={loading ? "Loading payments..." : "No payments found"}
        />
      </div>

      <SupplierPaymentDrawer
        isOpen={showDrawer}
        supplierId={supplierId}
        onClose={() => { setShowDrawer(false); setSelectedPayment(null); }}
        onSave={() => { setShowDrawer(false); setSelectedPayment(null); }}
        payment={selectedPayment}
      />
    </div>
  );
};

export default SupplierPayment;